import { useQuery } from '@tanstack/react-query'
import { fetchFeaturedSlideDeckMaterials } from '@breakoutlearning/firebase-repository/firestore/SlideDeckMaterial'
import { query } from 'config/api'
import type { useMeetingResultsCubit } from 'hooks/cubits/meetingsResults'

type MeetingResultsCubit = ReturnType<typeof useMeetingResultsCubit>

export function useGradeExplainer({ cubit }: { cubit: MeetingResultsCubit }) {
  const assignmentId = cubit.meetingCubit.assignmentId ?? ''
  const sectionId = cubit.meetingCubit.sectionId ?? ''
  const slideDeckId = cubit.slideDeckId
  const roomId = cubit.roomStateId ?? ''
  const viewerId = cubit.meetingCubit.currentUser.uid
  const isStaffView = cubit.userId !== viewerId
  const enabled = Boolean(assignmentId && roomId && (!isStaffView || sectionId))
  const params = { assignmentId, roomId }
  const staffParams = {
    sectionId,
    assignmentId,
    roomId,
    userId: cubit.userId,
  }
  const explanationOptions = isStaffView
    ? query.api.v1.sections.assignments.rooms.users.gradeExplanation.show.queryOptions(
        { params: staffParams }
      )
    : query.api.v1.assignments.rooms.gradeExplanation.show.queryOptions({
        params,
      })
  const detailsOptions = isStaffView
    ? query.api.v1.sections.assignments.rooms.users.gradeDetails.show.queryOptions(
        { params: staffParams }
      )
    : query.api.v1.assignments.rooms.gradeDetails.show.queryOptions({ params })
  const explanationCacheIdentity = {
    ...explanationOptions.queryKey[1],
    viewerId,
    studentId: cubit.userId,
  }
  const detailsCacheIdentity = {
    ...detailsOptions.queryKey[1],
    viewerId,
    studentId: cubit.userId,
  }

  const explanation = useQuery({
    ...explanationOptions,
    queryKey: [explanationOptions.queryKey[0], explanationCacheIdentity],
    enabled,
    retry: (failureCount, error) =>
      error.status !== 403 && error.status !== 404 && failureCount < 3,
  })
  const details = useQuery({
    ...detailsOptions,
    queryKey: [detailsOptions.queryKey[0], detailsCacheIdentity],
    enabled,
    retry: (failureCount, error) =>
      error.status !== 403 && error.status !== 404 && failureCount < 3,
  })
  const heroImage = useQuery({
    queryKey: ['grade-explainer-hero-image', slideDeckId],
    enabled: Boolean(slideDeckId),
    queryFn: async () => {
      const featured = await fetchFeaturedSlideDeckMaterials(cubit.repository, {
        slideDeckId,
      })
      return (
        featured.find((material) => material.data.materialLink)?.data
          .materialLink ?? null
      )
    },
  })

  return {
    explanation:
      explanation.data && !('error' in explanation.data)
        ? explanation.data
        : null,
    explanationUnavailable: explanation.error?.status === 404,
    explanationError:
      explanation.error && explanation.error.status !== 404
        ? explanation.error
        : null,
    explanationPending: explanation.isPending,
    details: details.data && !('error' in details.data) ? details.data : null,
    detailsError: details.error,
    detailsPending: details.isPending,
    heroImageUrl: heroImage.data ?? null,
    retry: () => {
      void explanation.refetch()
      void details.refetch()
    },
  }
}
